/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { TFunction } from 'next-i18next'

import { IWebsiteProduct } from '@src/components/product-card/product-card.types'
import { BUILD_ENVIRONMENTS } from '@src/config/constants'
import { CONFIG_BUILD_ENV } from '@src/config/env'
import { FULL_REVIEW_PRODUCT_TYPES } from '@src/quince-components/src/atoms/reviews/createReview/constants/constant'
import {
  getDiscountedPrice,
  getInitialSelectedOptions,
} from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import { IBaseProductVariant } from '@src/quince-site-common/src/domains/ProductVariant/BaseProductVariant'
import {
  getKebabCaseURLFromInputString,
  getURLStringFromInputString,
} from '@src/quince-site-common/src/utils/getURLStringFromInputString'
import { ensureDynamicImageInCarousel } from '@src/quince-site-pdp/src/organisms/productPageSections/productImages/productImages.helper'
import {
  pidAndSockDonationCostMap,
  TUniquePricingMap,
  TUniqueVariantPricing,
} from '@src/quince-site-pdp/src/organisms/productPageSections/transparentPricing/TransparentPricing'

import PriceDetailsForSSR, { PriceDetailsMapValue } from '../domains/PriceDetailsForSSR'
import Product, { ProductLite } from '../domains/Product'
import BaseProduct from '../domains/Product/BaseProduct'
import Bundle, { PRODUCT_TYPES } from '../domains/Product/BundleProduct'
import ProductComparisonBrand, { ComparisonBrandPrice } from '../domains/ProductComparisonBrand'
import ProductImage from '../domains/ProductImage'
import ProductMedia from '../domains/ProductMedia'
import { ProductOption, ProductOptionName } from '../domains/ProductOption'
import ProductTag from '../domains/ProductTag'
import { TransparentPricingItem } from '../domains/ProductTransparentPricing'
import { BaseProductVariant, ProductVariantOption } from '../domains/ProductVariant'
import ProductVideo from '../domains/ProductVideo'
import { getAdditionalProductTags, getFestivalOfferPrice } from '../utils/festivalOffersUtility'
import formatDisplayAmountValue from '../utils/formatDisplayAmountValue'
import { GenderName, getCurrentPageGenderContext } from '../utils/getCurrentPageGenderContext'
import isBrowser from '../utils/isBrowser'
import keyBy from '../utils/keyBy'
import { isProductImage } from '../utils/typeGuards'

import type { Document } from '@contentful/rich-text-types'

// @i18n-ignore
export const THIRD_PARTY = '3P'

export enum CUSTOM_FIELD_NAMES {
  HIDE_TRANSPARENT_PRICING = 'HIDE_TRANSPARENT_PRICING',
  HIDE_COMP_PRICING = 'HIDE_COMP_PRICING',
  SHOW_DOLLAR_COMP_PRICING = 'SHOW_DOLLAR_COMP_PRICING',
  HIDE_FREE_RETURN_COMP_PRICING = 'HIDE_FREE_RETURN_COMP_PRICING',
  IS_MTO = 'IS_MTO',
  MADE_TO_ORDER_TEXT = 'MADE_TO_ORDER_TEXT',
  SHOW_LARGE_REVIEW_FORM = 'SHOW_LARGE_REVIEW_FORM',
  SHIPPING_INFO_TEXT = 'SHIPPING_INFO_TEXT',
  IS_RETURNABLE = 'IS_RETURNABLE',
  SUB_DESCRIPTION_TEXT = 'SUB_DESCRIPTION_TEXT',
  ORDER_SUMMARY_TEXT = 'ORDER_SUMMARY_TEXT',
  OFFER_MESSAGE = 'OFFER_MESSAGE',
  BRAND_NAME = 'BRAND_NAME',
  BRAND_LINK = 'BRAND_LINK',
  VARIANT_OFFER_MESSAGE = 'VARIANT_OFFER_MESSAGE',
  TRADITIONAL_DISCOUNT_MESSAGE = 'TRADITIONAL_DISCOUNT_MESSAGE',
  TRADITIONAL_DISCOUNT_MESSAGE_VARIANT = 'TRADITIONAL_DISCOUNT_MESSAGE_VARIANT',
  IS_NON_PHYSICAL_PRODUCT = 'IS_NON_PHYSICAL_PRODUCT',
  MTO_LEAD_TIME = 'MTO_LEAD_TIME',
  AGE_LIMIT_IN_YEARS = 'AGE_LIMIT_IN_YEARS',
  PDP_CUSTOM_BANNER_TEXT = 'PDP_CUSTOM_BANNER_TEXT',
  PDP_CUSTOM_MORE_INFO_TEXT = 'PDP_CUSTOM_MORE_INFO_TEXT',
}

export const CUSTOM_FIELD_NAME_TO_FIELD_ID_MAP = {
  [CUSTOM_FIELD_NAMES.HIDE_TRANSPARENT_PRICING]: 2,
  [CUSTOM_FIELD_NAMES.HIDE_COMP_PRICING]: 20,
  [CUSTOM_FIELD_NAMES.HIDE_FREE_RETURN_COMP_PRICING]: 6,
  [CUSTOM_FIELD_NAMES.SHOW_DOLLAR_COMP_PRICING]: 4,
  [CUSTOM_FIELD_NAMES.IS_MTO]: 19,
  [CUSTOM_FIELD_NAMES.MADE_TO_ORDER_TEXT]: 3,
  [CUSTOM_FIELD_NAMES.SHOW_LARGE_REVIEW_FORM]: 10,
  [CUSTOM_FIELD_NAMES.SHIPPING_INFO_TEXT]: 5,
  [CUSTOM_FIELD_NAMES.IS_RETURNABLE]: 22,
  [CUSTOM_FIELD_NAMES.SUB_DESCRIPTION_TEXT]: 23,
  [CUSTOM_FIELD_NAMES.ORDER_SUMMARY_TEXT]: 5,
  [CUSTOM_FIELD_NAMES.OFFER_MESSAGE]: 26,
  [CUSTOM_FIELD_NAMES.BRAND_NAME]: 32,
  [CUSTOM_FIELD_NAMES.BRAND_LINK]: 33,
  [CUSTOM_FIELD_NAMES.VARIANT_OFFER_MESSAGE]: 31,
  [CUSTOM_FIELD_NAMES.TRADITIONAL_DISCOUNT_MESSAGE]: 34,
  [CUSTOM_FIELD_NAMES.TRADITIONAL_DISCOUNT_MESSAGE_VARIANT]: 35,
  [CUSTOM_FIELD_NAMES.IS_NON_PHYSICAL_PRODUCT]: 21,
  [CUSTOM_FIELD_NAMES.MTO_LEAD_TIME]: 134,
  [CUSTOM_FIELD_NAMES.PDP_CUSTOM_BANNER_TEXT]: 144,
  [CUSTOM_FIELD_NAMES.PDP_CUSTOM_MORE_INFO_TEXT]: 145,
  [CUSTOM_FIELD_NAMES.AGE_LIMIT_IN_YEARS]: 146,
}

export const SUB_DEPARTMENT_ID = {
  VITAMINS: 381,
  ALCOHOL: 399,
}
export const QA_SUB_DEPARTMENT_ID = {
  VITAMINS: 268,
  ALCOHOL: 273,
}

export const CLASS_ID = {
  DOG_FOOD: 983,
}

export const QA_CLASS_ID = {
  DOG_FOOD: 620,
}

export const BEAUTY_PRODUCTS_INFO = {
  DEPARTMENT_ID_PROD: 108,
  DIVISION_ID_PROD: 28,
  DEPARTMENT_ID_QA: 77,
  DIVISION_ID_QA: 26,
}

const UNIT_LABELS = {
  SERVING: 'serving',
  OUNCE: 'ounce',
}

// @i18n-ignore
const NAME_KEY_TEXT = 'name'

class ProductHelper {
  getAllPossibleImageOptionName = (product: Product) => {
    const allPossibleImageOptionName = new Set()
    product.images.forEach(image => {
      image.options.forEach(option => {
        allPossibleImageOptionName.add(getURLStringFromInputString(option.name))
      })
    })
    return allPossibleImageOptionName
  }

  getSpid(product: BaseProduct, options: ProductVariantOption[]): number {
    const [firstVariant] = this.getVariantsForOptions(product, options)
    const spid = firstVariant?.spid || product.spid
    return spid
  }

  /**
   * This returns all in case no option is provided, Also in case an option is not given, then that will not take part in filtering
   */
  getVariantsForOptions(product: BaseProduct | Bundle, options?: ProductVariantOption[]): BaseProductVariant[] {
    if (!options || product.type === PRODUCT_TYPES.BUNDLE) return []
    return (product.variants || []).filter(variant => {
      // Ensure variant is an object and not null or undefined
      if (typeof variant !== 'object' || variant === null) return false
      for (const option of options) {
        // For customization options, since they are not part of variants, we can't filter those, hence returning always.
        if (
          option.name !== ProductOptionName.Customization &&
          option.name !== ProductOptionName.Engraving &&
          this.getOptionValueByNameInVariant(variant, option.name) !== option.value
        ) {
          return false
        }
      }
      return true
    })
  }

  /**
   * This method do the cascading only from top to bottom.
   */
  getCascadingProductOptions(product: BaseProduct, options: ProductVariantOption[]): ProductOption[] {
    const cascadedProductOptions: ProductOption[] = []
    // To start with for first level, not doing any filtering.
    const previousLevelOptionNames = new Set<string>()
    ;[...product.productOptions]
      .sort((vo1, vo2) => vo1.displaySequence - vo2.displaySequence)
      .forEach(productOption => {
        // Getting Filtered Variants by selected options for previous levels.
        const filteredVariants = this.getVariantsForOptions(
          product,
          options.filter(o => previousLevelOptionNames.has(o.name)),
        )
        const cascadedOptionValues = filteredVariants.map(v =>
          this.getOptionValueByNameInVariant(v, productOption.name),
        )
        // For customization options, since they are not part of variants, we can't filter those, hence returning always.
        if (
          productOption.name === ProductOptionName.Customization ||
          productOption.name === ProductOptionName.Engraving
        ) {
          cascadedProductOptions.push(productOption)
        } else {
          cascadedProductOptions.push(
            new ProductOption({
              ...productOption,
              // Adding applicable options that we get after applying filtering
              values: productOption.values?.filter(v => cascadedOptionValues.includes(v.value)) || [],
            }),
          )
        }
        // Adding this option name for further filtering.
        previousLevelOptionNames.add(productOption.name)
      })
    return cascadedProductOptions
  }

  getBrandComparison(product?: Product, options?: ProductVariantOption[]): ProductComparisonBrand[] {
    if (!product) {
      return []
    }
    const variants = this.getVariantsForOptions(product, options)
    const getPrice = (v: BaseProductVariant) => v.price
    const minPrice = Math.min(...variants.map(getPrice))
    const variantId = variants.find(v => getPrice(v) === minPrice)?.id
    if (!variantId) {
      return []
    }
    return (product.comparisonBrands || [])
      .map(cb => {
        const filteredComparisonPrices: ComparisonBrandPrice[] = []
        const brandPrice = ProductComparisonBrand.getPriceForVariant(cb, variantId)
        if (brandPrice && brandPrice.price > 0) {
          filteredComparisonPrices.push(brandPrice)
        }
        return new ProductComparisonBrand({ ...cb, prices: filteredComparisonPrices })
      })
      .filter(cb => cb.prices.length > 0)
  }

  getQuincePriceForOptions(
    product: BaseProduct,
    options?: ProductVariantOption[],
    svidPriceMap?: Record<number, number>,
  ): number {
    return Math.min(
      ...this.getVariantsForOptions(product, options).map(v => {
        return svidPriceMap?.[v.svid] || v.price
      }),
    )
  }

  getQuincePriceForBundleOptions(bundle: Bundle, selectedOptions: ProductVariantOption[]) {
    const customBundleProducts = bundle.customBundleProducts || []
    const discount = bundle.discountPrice || 0

    const productsSumPrice = customBundleProducts.reduce((acc, product) => {
      const { salePrice } = this.getProductPriceDetails(product, selectedOptions)
      // Checking for finite since the selected option might not be present in the product
      if (!isFinite(salePrice)) {
        // It will be the minimum price
        const { salePrice } = this.getProductPriceDetails(product, [])
        return acc + salePrice
      }
      return acc + salePrice
    }, 0)

    return +formatDisplayAmountValue(+getDiscountedPrice(Number(productsSumPrice), discount))
  }

  getMinPriceForBundle(bundle: Bundle): number {
    const price = bundle.customBundleProducts?.reduce((acc, product) => {
      return (
        acc +
        Math.min(
          ...this.getVariantsForOptions(product, []).map(v => {
            return v.price
          }),
        )
      )
    }, 0)
    return price || 0
  }

  getSelectedMergeOptions(product: Product, selectedSiblingProductSpid?: number) {
    return product.addOnProductOptionGroups?.map(group => {
      return {
        name: group.groupOptionName,
        value: group.groupItems.find(item => item.spid === selectedSiblingProductSpid)?.mergeOptionValue,
      }
    })
  }

  hasDifferentPrices(product: BaseProduct, options: ProductVariantOption[]): boolean {
    const variants = this.getVariantsForOptions(product, options)
    let lastPrice = variants[0]?.price
    for (let index = 1; index < variants.length; index++) {
      const variant = variants[index]
      if (lastPrice !== variant?.price) {
        return true
      }
      lastPrice = variant?.price
    }
    return false
  }

  getTraditionalPriceForOptions(product: Product, options: ProductVariantOption[]) {
    const allBrands = this.getBrandComparison(product, options)
    const showOnSiteBrands = allBrands.filter(cb => cb.showOnSite)
    const price = Math.min(
      ...(showOnSiteBrands.length > 0 ? showOnSiteBrands : allBrands).map(bc =>
        Math.min(...bc.prices.map(cp => cp.price)),
      ),
    )
    if (!isNaN(price) && price != Infinity) {
      return price
    }
    return undefined
  }

  getOptionSelectedValueMap(product: BaseProduct | Bundle, options: ProductVariantOption[]) {
    const updatedSelectedOptions = getInitialSelectedOptions(product, options, true)
    const optionSelectedValueMap: Record<string, string> = updatedSelectedOptions.reduce((map, option) => {
      return {
        ...map,
        [option.name]: option.value,
      }
    }, {})
    return optionSelectedValueMap
  }

  getFilteredMedia(
    pma: ProductMedia[],
    optionSelectedValueMap: Record<string, string>,
    gender?: GenderName | undefined,
  ): ProductMedia[] {
    const productMedia = (pma || []).filter(pm => {
      for (const imageOption of pm.options) {
        // Values of current option for which this image is applicable
        const applicableValues = imageOption.values
        // Current selected value on PDP page
        const selectedValue = optionSelectedValueMap[imageOption.name]
        if (selectedValue && !applicableValues.includes(selectedValue)) {
          return false
        }
      }

      const media = isProductImage(pm) ? pm.image : pm.video

      if (media.description === GenderName.MALE || media.description === GenderName.FEMALE) {
        let currentPageGenderContext: GenderName | undefined
        if (gender) {
          currentPageGenderContext = gender
        } else if (isBrowser()) {
          const pathname = window.location.pathname
          const search = window.location.search
          currentPageGenderContext = getCurrentPageGenderContext(pathname, search)
        }

        if (currentPageGenderContext && currentPageGenderContext !== media.description) {
          return false
        }
      }
      return true
    })

    return productMedia
  }

  getImagesForOptions(
    product: BaseProduct | Bundle,
    options: ProductVariantOption[],
    gender?: GenderName | undefined,
  ): ProductImage[] {
    const optionSelectedValueMap = this.getOptionSelectedValueMap(product, options)
    const productMedia = this.getFilteredMedia(product.images, optionSelectedValueMap, gender)
    if (product.type === PRODUCT_TYPES.BUNDLE) return productMedia as ProductImage[]
    if (!optionSelectedValueMap[ProductOptionName.Customization]) {
      return productMedia as ProductImage[]
    } else {
      return ensureDynamicImageInCarousel(productMedia as ProductImage[], {
        char: optionSelectedValueMap[ProductOptionName.Customization],
        productId: product.spid,
        variantId: this.getVariantsForOptions(product, options)[0]?.svid,
        dynamicImages: product.dynamicImages ?? [],
      })
    }
  }

  getOptionValueByNameInVariant(variant: BaseProductVariant, optionName: ProductOptionName): string | undefined {
    return variant.options.find(x => x.name === optionName)?.value
  }

  getUpdatedSelectedOptionsOnProductOptionSelect(
    product: BaseProduct,
    currentSelectedOptions: ProductVariantOption[],
    selectedOptionName: ProductOptionName,
    selectedOptionValue: string,
  ) {
    const updatedSelectedOptions = currentSelectedOptions.filter(o => o.name !== selectedOptionName)
    updatedSelectedOptions.push(new ProductVariantOption({ name: selectedOptionName, value: selectedOptionValue }))
    return this.filterInvalidSelectedOptionsCombination(product, updatedSelectedOptions)
  }

  // TODO: Use internal product id instead of svid when it's available in backend api
  getTransparentPricingCostsForOptions(
    product: Product,
    options: ProductVariantOption[],
    pidAndSockDonationCostMap: Record<string, number> = {},
  ) {
    const variantsForOptions = this.getVariantsForOptions(product, options)
    const pidForSelectedVariant = variantsForOptions[0]?.productId || product.id
    const sockDonationCost = pidAndSockDonationCostMap[`${pidForSelectedVariant}`]
    const transparentPricingCostForOptions = product.transparentPricing?.items.find(
      item => item.variantId === variantsForOptions[0]?.id,
    )
    if (sockDonationCost && transparentPricingCostForOptions) {
      const transparentPricingItem = new TransparentPricingItem({
        ...transparentPricingCostForOptions,
        sockDonationCost,
      })
      return transparentPricingItem
    }
    return transparentPricingCostForOptions
  }

  getTransparentPricingCostsForProduct(
    product: Product,
    options: ProductVariantOption[],
    uniquePricingMap: TUniquePricingMap,
    variantIdToUniqueMap: TUniqueVariantPricing,
  ) {
    const variantsForOptions = this.getVariantsForOptions(product, options)
    const pidForSelectedVariant = variantsForOptions[0]?.productId || product.id
    const sockDonationCost =
      pidAndSockDonationCostMap[`${pidForSelectedVariant}` as keyof typeof pidAndSockDonationCostMap]
    const variantId = variantsForOptions[0]?.id

    if (!variantId || !variantIdToUniqueMap[variantId]) {
      return
    }

    const transparentPricingCostForOptions = uniquePricingMap[variantIdToUniqueMap[variantId]]
    if (sockDonationCost && transparentPricingCostForOptions) {
      const transparentPricingItem = new TransparentPricingItem({
        ...transparentPricingCostForOptions,
        sockDonationCost,
      })
      return transparentPricingItem
    }
    return transparentPricingCostForOptions
  }

  isSdcProduct = (product: Product) => {
    return Boolean(product.dynamicImages && product.dynamicImages.length > 0)
  }

  /**
   * This validates whether the input options combination is valid or not, if not valid, then removes the lowest in the sequence.
   */
  private filterInvalidSelectedOptionsCombination(product: BaseProduct, selectedOptions: ProductVariantOption[]) {
    // This returns the valid options in the option sequence.
    const cascadedProductOptions = this.getCascadingProductOptions(product, selectedOptions)
    const filteredOptions: ProductVariantOption[] = []
    cascadedProductOptions.forEach(productOption => {
      // Since customization is always valid, hence always added it as selected option.
      if (productOption.name === ProductOptionName.Customization) {
        const customizationOption = selectedOptions.find(opt => opt.name === ProductOptionName.Customization)

        if (customizationOption) {
          filteredOptions.push(customizationOption)
        }
      } else if (productOption.name === ProductOptionName.Engraving) {
        const engravingOption = selectedOptions.find(opt => opt.name === ProductOptionName.Engraving)
        if (engravingOption) {
          filteredOptions.push(engravingOption)
        }
      } else {
        const selectedOption = selectedOptions.find(
          o => o.name === productOption.name && productOption.values?.map(x => x.value).includes(o.value),
        )
        if (selectedOption) {
          filteredOptions.push(selectedOption)
        }
      }
    })
    return filteredOptions
  }

  getVideoForOptions(product: Product, options: ProductVariantOption[]): ProductVideo[] {
    const optionSelectedValueMap = this.getOptionSelectedValueMap(product, options)
    const productMedia = this.getFilteredMedia(product.videos, optionSelectedValueMap)
    return productMedia as ProductVideo[]
  }

  getApplicableTags({
    product,
    selectedOptions,
    productTags,
  }: {
    product?: BaseProduct | Bundle
    selectedOptions: ProductVariantOption[]
    productTags?: ProductTag[]
  }) {
    const baseProductTags = productTags || product?.tags || []
    const additionalProductTags = getAdditionalProductTags(product, selectedOptions)
    const selectedOptionNameVsValue: Record<string, ProductVariantOption> = keyBy(selectedOptions, NAME_KEY_TEXT)

    const tagsIncluded = new Set()

    const isTagApplicable = (tag: ProductTag) => {
      const applicableOptions = tag.options
      //For empty options list tab is applicable
      if (!applicableOptions || applicableOptions.length === 0) {
        return true
      }
      const applicableOptionsNameAndValuesMap: Record<string, string[]> = {}
      applicableOptions.forEach(ao => {
        const key: string = ao.name
        const values: string[] = applicableOptionsNameAndValuesMap[key] || []
        values.push(ao.value)
        applicableOptionsNameAndValuesMap[key] = values
      })

      let shouldAddTag = true
      Object.keys(selectedOptionNameVsValue).forEach(k => {
        if (
          applicableOptionsNameAndValuesMap[k] &&
          !applicableOptionsNameAndValuesMap[k].includes(selectedOptionNameVsValue[k]?.value || '')
        ) {
          shouldAddTag = false
        }
      })
      return shouldAddTag
    }

    const filteredBaseProductTags = baseProductTags.filter(tag => {
      if (!tagsIncluded.has(tag.value) && isTagApplicable(tag)) {
        tagsIncluded.add(tag.value)
        return true
      }
      return false
    })
    const isSoldOut = filteredBaseProductTags.some(tag => tag.value.toLowerCase() === 'sold out')
    const shouldShowAdditionalProductTags = !isSoldOut && additionalProductTags.length
    const allTags = shouldShowAdditionalProductTags ? additionalProductTags : filteredBaseProductTags
    return allTags
  }

  getTagValueWithFirstLetterUpperCase = (tag: string) => {
    if (typeof tag !== 'string' || !tag) return ''
    const firstLetter = tag.charAt(0).toUpperCase() || ''
    const restOfString = tag.slice(1).toLowerCase() || ''
    return firstLetter + restOfString
  }

  shouldShowFullReview = (product: Product | Bundle) => {
    const name = product.productTypeName?.toLowerCase()
    const match = FULL_REVIEW_PRODUCT_TYPES.filter(type => name?.includes(type))
    if (match.length > 0) return true
    return false
  }

  getSavingPercentage = (traditionalPrice: number, salePrice: number) => {
    return (((traditionalPrice - salePrice) * 100) / traditionalPrice).toFixed(0)
  }

  getMTOText(_product: Product, madeToOrderDocument?: Document, defaultMtoText?: string, t: TFunction) {
    if (!defaultMtoText) {
      defaultMtoText = t('Made_to_Order')
    }
    const mtoText = madeToOrderDocument && resolveFirstLevelTextFromDocument(madeToOrderDocument)
    return mtoText || defaultMtoText
  }

  isGiftCard(productId?: number) {
    return productId == 2206467817583
  }

  getShippingInfoText(product: Product) {
    const shippingInfoCustomFieldText = productHelper.getCustomFieldValue(
      product,
      CUSTOM_FIELD_NAMES.SHIPPING_INFO_TEXT,
    )
    return getFormattedCustomFieldText(shippingInfoCustomFieldText)
  }

  getOrderSummaryText(product: Product) {
    const shippingInfoCustomFieldText = productHelper.getCustomFieldValue(
      product,
      CUSTOM_FIELD_NAMES.ORDER_SUMMARY_TEXT,
    )
    return getFormattedCustomFieldText(shippingInfoCustomFieldText)
  }

  getSubDescriptionText(product: Product) {
    const shippingInfoCustomFieldText = productHelper.getCustomFieldValue(
      product,
      CUSTOM_FIELD_NAMES.SUB_DESCRIPTION_TEXT,
    )
    return getFormattedCustomFieldText(shippingInfoCustomFieldText)
  }

  getOfferMessage(product: Product) {
    const shippingInfoCustomFieldText = productHelper.getCustomFieldValue(product, CUSTOM_FIELD_NAMES.OFFER_MESSAGE)
    return getFormattedCustomFieldText(shippingInfoCustomFieldText)
  }

  getDiscountMessagesForSelectedOptions(product: Product, selectedOptions: ProductVariantOption[]) {
    const variantsForSelectedOptions = this.getVariantsForOptions(product, selectedOptions)
    let finalOfferMessage: string | undefined = undefined
    let finalTraditionalDiscountMessage: string | undefined = undefined

    if (variantsForSelectedOptions.length == 1) {
      const offerMessageForVariant = variantsForSelectedOptions[0]
        ? productHelper.getCustomFieldValue(
            variantsForSelectedOptions[0],
            CUSTOM_FIELD_NAMES.VARIANT_OFFER_MESSAGE,
            true,
          )
        : undefined

      const traditionalDiscountMessageForVariant = variantsForSelectedOptions[0]
        ? productHelper.getCustomFieldValue(
            variantsForSelectedOptions[0],
            CUSTOM_FIELD_NAMES.TRADITIONAL_DISCOUNT_MESSAGE_VARIANT,
            true,
          )
        : undefined

      finalOfferMessage = offerMessageForVariant ? String(offerMessageForVariant) : undefined
      finalTraditionalDiscountMessage = traditionalDiscountMessageForVariant
        ? String(traditionalDiscountMessageForVariant)
        : undefined
    }

    if (!finalOfferMessage) {
      const offerMessage = productHelper.getOfferMessage(product)
      finalOfferMessage = offerMessage ? String(offerMessage) : undefined
    }
    if (!finalTraditionalDiscountMessage) {
      const traditionalDiscountMessage = productHelper.getCustomFieldValue(
        product,
        CUSTOM_FIELD_NAMES.TRADITIONAL_DISCOUNT_MESSAGE,
        true,
      )
      finalTraditionalDiscountMessage = traditionalDiscountMessage ? String(traditionalDiscountMessage) : undefined
    }

    return { finalOfferMessage, finalTraditionalDiscountMessage }
  }

  getIsReturnable(product: Product) {
    const shippingInfoCustomFieldText = String(
      this.getCustomFieldValue(product, CUSTOM_FIELD_NAMES.IS_RETURNABLE, false, true),
    ).toLowerCase()
    if (shippingInfoCustomFieldText === 'false') return false
    return true
  }

  getProductMedia = (
    product: Product,
    selectedOptions: ProductVariantOption[],
    options?: {
      forcedVideoPosition?: number
    },
  ) => {
    const productImages = productHelper.getImagesForOptions(product, selectedOptions)
    const productVideo = productHelper.getVideoForOptions(product, selectedOptions)[0]

    const productMedia: ProductMedia[] = [...productImages]

    if (productVideo) {
      const forcedVideoIndex =
        options?.forcedVideoPosition && Math.min(options?.forcedVideoPosition - 1, productMedia.length)

      const videoIndex =
        forcedVideoIndex !== undefined ? forcedVideoIndex : productVideo.videoIndex || productImages.length

      productMedia.splice(videoIndex, 0, productVideo)
    }
    return productMedia
  }

  getProductSpidInDepth = (products: (BaseProduct | Bundle)[]) => {
    const spidWihChildSpid = products.reduce((productIdInDepth: number[], product: BaseProduct | Bundle) => {
      if (product.type === PRODUCT_TYPES.BUNDLE) return productIdInDepth
      productIdInDepth.push(...[product.spid, ...(product.childSpids || [])].filter(id => !!id))
      return productIdInDepth
    }, [])
    return spidWihChildSpid
  }

  getCustomFieldValue<T extends number | string = string | number>(
    product: Product | BaseProductVariant,
    customFieldName: CUSTOM_FIELD_NAMES,
    shouldFormat?: boolean,
    useFieldValue?: boolean,
  ): T | undefined {
    const customField = (product.customFields || []).find(
      cf => cf.fieldId === CUSTOM_FIELD_NAME_TO_FIELD_ID_MAP[customFieldName],
    )
    const customFieldValue = useFieldValue
      ? customField?.fieldValue
      : ((customField?.fieldValueDisplayName ?? customField?.fieldValue) as T)

    return shouldFormat ? getFormattedCustomFieldText<T>(customFieldValue) : customFieldValue
  }

  getIsTruthyCustomField(product: Product | undefined, customFieldName: CUSTOM_FIELD_NAMES): boolean {
    if (!product) return false
    return String(this.getCustomFieldValue(product, customFieldName, false, true)).toLowerCase() === 'true'
  }

  getIsAgeRestrictedProduct(product: Product) {
    if (!product) return false
    const ageLimitInYears = this.getCustomFieldValue(product, CUSTOM_FIELD_NAMES.AGE_LIMIT_IN_YEARS, false, true)
    return ageLimitInYears ? Number(ageLimitInYears) > 0 : false
  }

  getAgeLimitInYears(product: Product) {
    const ageLimitInYears = this.getCustomFieldValue(product, CUSTOM_FIELD_NAMES.AGE_LIMIT_IN_YEARS, false, true)
    return ageLimitInYears ? Number(ageLimitInYears) : undefined
  }

  getPDPCustomBannerText(product: Product) {
    const pdpCustomBannerText = this.getCustomFieldValue(
      product,
      CUSTOM_FIELD_NAMES.PDP_CUSTOM_BANNER_TEXT,
      false,
      true,
    )
    return pdpCustomBannerText ? String(pdpCustomBannerText) : undefined
  }

  getPDPCustomMoreInfoText(product: Product) {
    const pdpCustomMoreInfoText = this.getCustomFieldValue(
      product,
      CUSTOM_FIELD_NAMES.PDP_CUSTOM_MORE_INFO_TEXT,
      false,
      true,
    )
    return pdpCustomMoreInfoText ? String(pdpCustomMoreInfoText) : undefined
  }

  getPriceDetailsForSSR(product: Product): PriceDetailsForSSR {
    const selectedOptionsKeyToPriceDetailMap = this.getPriceDetailsMap(product)
    const productOptionNameSequence = this.getProductOptionNameSequence(product)
    return {
      selectedOptionsKeyToPriceDetailMap,
      productOptionNameSequence,
    }
  }

  getProductPriceDetails(product: Product, productOptions: ProductVariantOption[]): PriceDetailsMapValue {
    const defaultCurrency = '$' //Giving default value here, might change in future
    const salePrice = this.getQuincePriceForOptions(product, productOptions)
    const traditionalPrice = this.getTraditionalPriceForOptions(product, productOptions)
    const savingPercentage = traditionalPrice
      ? this.getSavePercentage(traditionalPrice, salePrice) || undefined
      : undefined
    const festivalOfferPrice =
      getFestivalOfferPrice({
        product,
        selectedOptions: productOptions,
      }) || undefined

    return {
      salePrice,
      festivalOfferPrice,
      formattedFestivalOfferPrice: festivalOfferPrice ? formatDisplayAmountValue(festivalOfferPrice) : undefined,
      formattedSalePrice: formatDisplayAmountValue(salePrice),
      formattedTraditionalPrice: traditionalPrice ? formatDisplayAmountValue(traditionalPrice) : undefined,
      savingPercentage,
      traditionalPrice,
      currency: defaultCurrency,
    }
  }

  getServingsForOptions(product: BaseProduct, options: ProductVariantOption[]): number {
    return Math.min(
      ...this.getVariantsForOptions(product, options).map(v => {
        return Number(v.servings)
      }),
    )
  }

  getProductOptionToDisplayInProductCard(cascadedProductOptionsOrProduct: ProductOption[] | BaseProduct) {
    const productOptions = Array.isArray(cascadedProductOptionsOrProduct)
      ? cascadedProductOptionsOrProduct
      : this.getCascadingProductOptions(cascadedProductOptionsOrProduct, [])

    return productOptions[0]
  }

  private getPriceDetailsMap(product: Product) {
    const allPossibleProductOptions = this.generateAllPossibleProductOptions(product)
    const priceDetailsMap: Record<string, PriceDetailsMapValue> = {}
    allPossibleProductOptions.forEach(possibleProductOptions => {
      const concatenatedKeyForSelectedOptions = this.getConcatenatedKeyForSelectedOptions(possibleProductOptions)
      priceDetailsMap[concatenatedKeyForSelectedOptions] = this.getProductPriceDetails(product, possibleProductOptions)
    })
    return priceDetailsMap
  }

  private generateAllPossibleProductOptions(product: Product) {
    const sameOptionNameApplicableProductOptionsMultiList: (ProductVariantOption[] | undefined)[] =
      product.productOptions.map(productOption =>
        productOption.values?.map(
          value =>
            new ProductVariantOption({
              name: productOption.name,
              value: value.value,
            }),
        ),
      )

    const allPossibleProductOptions: ProductVariantOption[][] = []

    sameOptionNameApplicableProductOptionsMultiList.forEach(sameOptionNameApplicableProductOptions => {
      const oldAllPossibleProductOptions = [...allPossibleProductOptions]
      sameOptionNameApplicableProductOptions?.forEach(productOption => {
        oldAllPossibleProductOptions.forEach(possibleProductOptions => {
          allPossibleProductOptions.push([...possibleProductOptions, productOption])
        })
      })

      allPossibleProductOptions.push(
        ...(sameOptionNameApplicableProductOptions || []).map(productOption => [productOption]),
      )
    })

    return allPossibleProductOptions
  }

  private getConcatenatedKeyForSelectedOptions(selectedOption: ProductVariantOption[]) {
    const stringifyOption = (option: ProductVariantOption) =>
      `${getKebabCaseURLFromInputString(option.name)}_${getKebabCaseURLFromInputString(option.value)}`
    return selectedOption.reduce((id, option) => {
      return id + (id ? '__' : '') + stringifyOption(option)
    }, '')
  }

  getProductOptionNameSequence(product: Product) {
    return product.productOptions
      .sort((productOption1, productOptions2) => productOption1.displaySequence - productOptions2.displaySequence)
      .map(productOption => getKebabCaseURLFromInputString(productOption.name))
  }

  private getSavePercentage(traditionalPrice: number, salePrice: number) {
    const percentage =
      traditionalPrice &&
      traditionalPrice > salePrice &&
      Math.round(((traditionalPrice - salePrice) / traditionalPrice) * 100)
    return percentage
  }

  calculateDiscount(originalPrice: number | string, discountedPrice: number | string) {
    const _originalPrice = Number(originalPrice)
    const _discountedPrice = Number(discountedPrice)
    const diff = _originalPrice - _discountedPrice
    if (diff > 0) {
      return (diff / _originalPrice) * 100
    } else return null
  }

  shouldShowDeliveryInformation(product: Product | ProductLite | Bundle): boolean {
    return Boolean(product.type === PRODUCT_TYPES.NORMAL && product.isValentineProduct)
  }

  updateProductOptionsWithNewColorOptions(
    product: Product,
    productOptions: ProductOption[],
    newColorGroupOptions: {
      displayName: string
      name: string
    }[],
    t: TFunction,
  ) {
    const coreColorOptionData = productOptions?.find(option => option?.name === ProductOptionName.Color)
    let additionalColorOptionsValues = [] as string[]
    const additionalDataOfnewColorGroupings = newColorGroupOptions.map(
      (colorGrouping: { displayName: string; name: string }) => {
        const colors = Array.from(
          new Set(
            product.variants
              ?.filter(variant => variant?.colorGrouping === colorGrouping?.name)
              ?.map(variant => variant?.options?.[0]?.value),
          ),
        ).filter(color => !!color)

        // colors is ordered based on the variant order.
        // use productOptions color order to maintain consistent color order.
        const orderedColors =
          coreColorOptionData?.values?.filter(color => colors.includes(color?.value))?.map(color => color?.value) || []

        additionalColorOptionsValues = [...orderedColors, ...additionalColorOptionsValues]
        return {
          ...coreColorOptionData,
          displayName: colorGrouping?.displayName,
          values: orderedColors
            ?.map(color => coreColorOptionData?.values?.find(option => option?.value == color))
            ?.filter(Boolean),
        }
      },
    )

    const colorOption = productOptions.find(option => option?.name === ProductOptionName.Color)
    const coreColorOptions = colorOption?.values?.filter(value => !additionalColorOptionsValues?.includes(value.value))
    productOptions = productOptions
      ?.map(option =>
        option?.name === ProductOptionName.Color
          ? coreColorOptions && coreColorOptions.length > 0
            ? {
                ...option,
                displayName: t('core_color'),
                values: coreColorOptions,
              }
            : undefined
          : option,
      )
      .filter(productOption => productOption !== undefined)

    const filteredProductOptions = [...productOptions, ...additionalDataOfnewColorGroupings]
    return filteredProductOptions
  }
}

const productHelper = new ProductHelper()

export default productHelper

export const getFormattedCustomFieldText = <T extends number | string>(
  customFieldText: T | undefined,
): T | undefined => {
  return customFieldText === '<p><br></p>' ? ('' as T) : customFieldText
}

const resolveFirstLevelTextFromDocument = (document: Document) => {
  if (!isBrowser()) return null
  const firstLevelNode = document.content[0]?.content?.[0]
  if (firstLevelNode?.nodeType === 'text') {
    return firstLevelNode.value
  }
  return null
}

export const isGiftCardCreditSupported = (variant: IBaseProductVariant | undefined) => {
  if (!variant) return false
  return variant.vendorType === THIRD_PARTY && variant.giftCardCredit && variant.giftCardCredit > 0
}

export const isProductAFurniture = (product: Product | undefined): boolean => {
  return product?.classification?.department?.id == 38
}

export const getIsWhiteGloveDelivery = (product: Product | undefined) => product?.isWhiteGloveDeliveryAvailable

export const getColorFromSelectedOptions = (selectedOptions: ProductVariantOption[]) => {
  return selectedOptions.find(option => option.name === ProductOptionName.Color)?.value || ''
}

export const isEngravingProduct = (selectedOptions: ProductVariantOption[]) => {
  if (selectedOptions.find(option => option.name === ProductOptionName.Customization)) return true
  return false
}

export const isProductVitamin = (product?: Product) => {
  const subDepartmentId = product?.classification?.subDepartment?.id

  if (CONFIG_BUILD_ENV === 'qa') {
    return subDepartmentId === QA_SUB_DEPARTMENT_ID.VITAMINS
  }
  return subDepartmentId === SUB_DEPARTMENT_ID.VITAMINS
}

export const isProductDogFood = (product?: Product) => {
  const classification = product?.classification
  const classId = classification?.class?.id

  if (CONFIG_BUILD_ENV === BUILD_ENVIRONMENTS.QA) {
    return classId === QA_CLASS_ID.DOG_FOOD
  }
  return classId === CLASS_ID.DOG_FOOD
}

export const shouldShowPricePerUnit = (product?: Product) => {
  const isVitamin = isProductVitamin(product)
  const isDogFood = isProductDogFood(product)
  return isVitamin || isDogFood
}

export const getUnitForPerServing = (product?: Product) => {
  // This is a temporary fix to show the unit for per serving. This will be removed in the future.
  if (isProductDogFood(product)) return UNIT_LABELS.OUNCE
  if (isProductVitamin(product)) return UNIT_LABELS.SERVING

  return ''
}

export const isProductBeauty = (product?: Product | IWebsiteProduct) => {
  if (!product?.classification) return false
  //if type is Product

  const deptIdBasedOnConfig =
    CONFIG_BUILD_ENV === 'qa' ? BEAUTY_PRODUCTS_INFO.DEPARTMENT_ID_QA : BEAUTY_PRODUCTS_INFO.DEPARTMENT_ID_PROD
  const divIdBasedOnConfig =
    CONFIG_BUILD_ENV === 'qa' ? BEAUTY_PRODUCTS_INFO.DIVISION_ID_QA : BEAUTY_PRODUCTS_INFO.DIVISION_ID_PROD

  if (
    'department' in product.classification &&
    'division' in product.classification &&
    typeof product.classification.department === 'object' &&
    typeof product.classification.division === 'object'
  ) {
    return (
      product?.classification?.department?.id === deptIdBasedOnConfig &&
      product?.classification?.division?.id === divIdBasedOnConfig
    )
    //if type is IWebsiteProduct
  } else if ('department_id' in product.classification && 'division_id' in product.classification) {
    return (
      product?.classification?.department_id === deptIdBasedOnConfig &&
      product?.classification?.division_id === divIdBasedOnConfig
    )
  }
  return false
}

export const isProductAlcohol = (product?: Product) => {
  const subDepartmentId = product?.classification?.subDepartment?.id
  if (CONFIG_BUILD_ENV === BUILD_ENVIRONMENTS.QA) {
    return subDepartmentId === QA_SUB_DEPARTMENT_ID.ALCOHOL
  }
  return subDepartmentId === SUB_DEPARTMENT_ID.ALCOHOL
}

export const getIsSelectedColorAppExclusive = (
  productOptions?: ProductOption[],
  selectedOptions?: ProductVariantOption[],
): boolean => {
  const selectedColor = selectedOptions?.find(option => option.name === ProductOptionName.Color)
  if (!selectedColor) return false

  const colorOption = productOptions?.find(option => option.name === ProductOptionName.Color)
  const colorValue = colorOption?.values?.find(value => value.value === selectedColor.value)

  return colorValue?.appExclusive ?? false
}

/* eslint-enable @typescript-eslint/no-unnecessary-condition */
/* eslint-enable @typescript-eslint/prefer-nullish-coalescing */
